import { type CSSProperties, type KeyboardEvent, memo, useRef } from 'react';

import { useGlass } from '@components/glass/useGlass';
import { Icon } from '@components/ui/Icon';
import type { Service } from '@typings/content';
import { cx } from '@utils/cx';

import styles from './ServiceOrbit.module.scss';

interface OrbitItemProps {
  readonly service: Service;
  readonly index: number;
  readonly isActive: boolean;
  readonly panelId: string;
  readonly tabId: string;
  readonly onSelect: (index: number) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

type SlotStyle = CSSProperties & Record<'--i', string>;

/**
 * One service on the ring: a round, icon-only glass button that is also
 * a tab. Its name is visually hidden but announced, and shown as a
 * tooltip on hover.
 *
 * The slot is a zero-size point on the circle, placed by CSS from `--i`;
 * the button centres itself on that point. Hover and focus select it as well
 * as click, so the centre answers wherever attention goes.
 *
 * Frost, not refraction: the button is always moving, and a refracting
 * surface re-runs its displacement filter every frame it moves.
 */
export const OrbitItem = memo(
  ({ service, index, isActive, panelId, tabId, onSelect, onKeyDown }: OrbitItemProps) => {
    const ref = useRef<HTMLButtonElement>(null);
    const glass = useGlass(ref, {
      variant: 'clear',
      refract: false,
      className: cx(styles.item, isActive && styles.active),
    });
    const slotStyle: SlotStyle = { '--i': String(index) };
    const select = (): void => {
      onSelect(index);
    };

    return (
      <div className={styles.slot} style={slotStyle} role="presentation">
        <button
          ref={ref}
          type="button"
          role="tab"
          id={tabId}
          aria-selected={isActive}
          aria-controls={panelId}
          tabIndex={isActive ? 0 : -1}
          title={service.title}
          data-index={index}
          className={glass.className}
          style={glass.style}
          data-glass-surface=""
          data-reveal=""
          onClick={select}
          onFocus={select}
          onPointerEnter={select}
          onKeyDown={onKeyDown}
        >
          {glass.filter}
          <Icon name={service.icon} className={styles.itemIcon} />
          <span className={styles.itemLabel}>{service.shortTitle}</span>
        </button>
      </div>
    );
  },
);

OrbitItem.displayName = 'OrbitItem';
